import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditProviderProductCodeModal from '@modals/CreateOrEditProviderProductCodeModal'

import { ProviderProductCodeModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditProviderProductCodeModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = <ModalName extends keyof typeof componentCallbacks>(component: ModalName, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditProviderProductCodeModal: {
      closeModal: () => setState({ showCreateOrEditProviderProductCodeModal: false }),
      createProviderProductCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteProviderProductCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateProviderProductCode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditProviderProductCodeModal } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateOrEditProviderProductCodeModal: (
        payload: { providerProductCode: Partial<ProviderProductCodeModel> },
      ) => {
        setModalData('CreateOrEditProviderProductCodeModal', payload)
        setState({ showCreateOrEditProviderProductCodeModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditProviderProductCodeModal
          callbacks={callbacks('CreateOrEditProviderProductCodeModal', setState)}
          showModal={showCreateOrEditProviderProductCodeModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
